import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type Props = {
  title: string;
  subtitle: string;
  children: ReactNode;
  footerText: string;
  footerLinkLabel: string;
  footerLinkTo: string;
  imageUrl: string;
  imageAlt: string;
  quote: string;
  quoteAuthor: string;
};

export default function AuthLayout({
  title,
  subtitle,
  children,
  footerText,
  footerLinkLabel,
  footerLinkTo,
  imageUrl,
  imageAlt,
  quote,
  quoteAuthor,
}: Props) {
  return (
    <div className="min-h-screen bg-background-50 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
      {/* Form side */}
      <div className="flex flex-col px-6 md:px-12 lg:px-16 py-8 md:py-10">
        <header className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500">
              <i className="ri-leaf-line text-lg text-background-50"></i>
            </span>
            <span className="font-heading text-xl font-semibold tracking-tight text-foreground-950">
              PureNest
            </span>
          </Link>
          <Link
            to="/"
            className="hidden md:inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-600 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to home
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-md">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] tracking-widest uppercase font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
              PureNest account
            </span>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl font-medium leading-[1.05] mt-4">
              {title}
            </h1>
            <p className="mt-3 text-foreground-700 text-base leading-relaxed">{subtitle}</p>

            <div className="mt-8">{children}</div>

            <p className="mt-8 text-sm text-foreground-700">
              {footerText}{" "}
              <Link
                to={footerLinkTo}
                className="text-primary-600 font-medium hover:text-primary-700"
              >
                {footerLinkLabel}
              </Link>
            </p>
          </div>
        </div>

        <footer className="text-xs text-foreground-600 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} PureNest. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-foreground-900">Privacy</a>
            <a href="#" className="hover:text-foreground-900">Terms</a>
            <a href="#" className="hover:text-foreground-900">Help</a>
          </div>
        </footer>
      </div>

      {/* Visual side */}
      <div className="hidden lg:block p-5">
        <div className="relative h-full w-full rounded-[28px] overflow-hidden">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/30 via-foreground-950/15 to-foreground-950/65"></div>

          <div className="relative z-10 h-full w-full flex flex-col justify-between p-10">
            <div className="flex items-center gap-2 text-background-50/90 text-xs tracking-widest uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-400"></span>
              Trusted by 12,400+ homes
            </div>

            <div className="space-y-6">
              <blockquote className="font-heading text-background-50 text-3xl md:text-4xl leading-[1.15]">
                "{quote}"
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-background-50/15 backdrop-blur-sm flex items-center justify-center text-background-50">
                  <i className="ri-double-quotes-l text-xl"></i>
                </div>
                <div>
                  <p className="text-background-50 text-sm font-medium">{quoteAuthor}</p>
                  <p className="text-background-50/70 text-xs">PureNest member since 2024</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-background-50/20">
                <div>
                  <p className="font-heading text-background-50 text-2xl">4.9</p>
                  <p className="text-background-50/70 text-xs mt-1">Avg rating</p>
                </div>
                <div>
                  <p className="font-heading text-background-50 text-2xl">320+</p>
                  <p className="text-background-50/70 text-xs mt-1">Vetted cleaners</p>
                </div>
                <div>
                  <p className="font-heading text-background-50 text-2xl">98%</p>
                  <p className="text-background-50/70 text-xs mt-1">Rebook rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}